import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { FeedState } from '../hooks/useAssetFeed';
import { newInstanceId, track } from '../lib/analytics';
import { ListAnalyticsContext, type ListContext } from '../lib/impressions';
import { MOBILE_QUERY, applySphereDiameter } from '../lib/sphere';
import { strings } from '../lib/strings';
import { EmptyState } from './EmptyState';
import { AlertIcon } from './Icons';
import { RollLabel } from './RollLabel';
import { SkeletonDisc, UniverseDisc } from './UniverseDisc';

const SKELETON_COUNT = 21;
/** Below this many items the list is short enough that an end marker is noise. */
const END_MARKER_MIN = 12;

/** Live column count of a CSS grid, so placeholders can complete the current row. */
function useGridColumns(ref: RefObject<HTMLElement>) {
  const [columns, setColumns] = useState(2);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const tracks = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length;
      setColumns((prev) => (tracks > 0 && tracks !== prev ? tracks : prev));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return columns;
}

/** Keeps `--sphere-d` in step with the grid width and the resolved `--disc-min`. */
function useSphereDiameter(gridRef: RefObject<HTMLElement>, probeRef: RefObject<HTMLElement>, mounted: boolean) {
  useLayoutEffect(() => {
    const grid = gridRef.current;
    const probe = probeRef.current;
    if (!grid || !probe) return;
    const measure = () => applySphereDiameter(grid, probe);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    observer.observe(probe);
    const phone = window.matchMedia(MOBILE_QUERY);
    phone.addEventListener('change', measure);
    return () => {
      observer.disconnect();
      phone.removeEventListener('change', measure);
    };
  }, [gridRef, probeRef, mounted]);
}

export interface GridAnalytics {
  surface: ListContext['surface'];
  sort: string;
  /** Changes whenever the list is a different result set (query, collection). */
  listKey: string;
}

export function UniverseGrid({ feed, label, analytics }: { feed: FeedState; label: string; analytics: GridAnalytics }) {
  const sentinel = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const columns = useGridColumns(listRef);
  const { items, total, pending, loadingMore, hasMore, error, moreFailed, pages, loadMore, retry } = feed;
  const showsGrid = !(error && !pending) && !(!pending && items.length === 0);
  useSphereDiameter(listRef, probeRef, showsGrid);

  const { surface, sort, listKey } = analytics;
  const list = useMemo<ListContext>(
    () => ({ surface, sort, listInstance: newInstanceId() }),
    // A fresh instance per result set, so impressions restart with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surface, sort, listKey]
  );

  useEffect(() => {
    if (pages > 0) track('feed_page_loaded', { surface, sort, list_instance: list.listInstance, page: pages, items_loaded: items.length, total_known: total ?? 'unknown', has_more: hasMore });
    // One event per page arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, list]);

  useEffect(() => {
    if (error) track('feed_error', { surface, sort, status: error.status });
    if (moreFailed) track('feed_error', { surface, sort, status: 'load_more_failed' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, moreFailed]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || moreFailed) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    }, { rootMargin: '800px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, moreFailed, loadMore]);

  if (error && !pending) {
    const missing = error.status === 404;
    return (
      <EmptyState
        kind={missing ? 'locked' : 'offline'}
        message={missing ? strings.unavailable : strings.temporarilyUnavailable}
        hint={missing ? undefined : strings.temporarilyUnavailableHint}
        onRetry={missing ? undefined : retry}
      />
    );
  }

  const firstLoad = pending && items.length === 0;
  if (!pending && items.length === 0) {
    return <EmptyState kind="search" message={strings.noResults} hint={strings.noResultsHint} />;
  }

  // Finish the partial row, then add one full row, so the wall reads as continuing.
  const remainder = items.length % columns;
  const placeholders = loadingMore ? (remainder ? columns - remainder : 0) + columns : 0;
  const reachedEnd = !firstLoad && !pending && !hasMore && items.length >= END_MARKER_MIN;

  return (
    <ListAnalyticsContext.Provider value={list}>
      <span ref={probeRef} className="sphere-probe" aria-hidden="true" />
      <ul
        ref={listRef}
        aria-label={label}
        aria-busy={pending || loadingMore}
        className="universe-grid m-0 p-0 transition-[opacity,filter] duration-base ease-standard"
        style={{
          opacity: pending && !firstLoad ? 0.45 : 1,
          filter: pending && !firstLoad ? 'saturate(0.6)' : 'none',
        }}
      >
        {firstLoad
          ? Array.from({ length: SKELETON_COUNT }, (_, i) => <SkeletonDisc key={i} index={i} />)
          : items.map((asset, i) => <UniverseDisc key={asset.id} asset={asset} index={i} />)}
        {Array.from({ length: placeholders }, (_, i) => (
          <SkeletonDisc key={`more-${i}`} index={i} entering />
        ))}
      </ul>
      <div ref={sentinel} className="h-px" aria-hidden="true" />

      <div className="flex min-h-[64px] items-center justify-center pt-12" aria-live="polite">
        {loadingMore && <span className="sr-only">{strings.loadingMore}</span>}
        {moreFailed && !loadingMore && (
          <div className="rise-in flex flex-wrap items-center justify-center gap-3 text-sm text-muted">
            <span className="flex items-center gap-2">
              <AlertIcon className="h-4 w-4 shrink-0 text-subtle" />
              {strings.loadMoreFailed}
            </span>
            <button
              type="button"
              onClick={loadMore}
              className="cta pressable rounded-full bg-fill px-4 py-1 font-semibold text-primary [--cta-fill:var(--color-primary)]"
            >
              <RollLabel>{strings.tryAgain}</RollLabel>
            </button>
          </div>
        )}
        {reachedEnd && (
          <p className="rise-in flex w-full max-w-search items-center gap-4 text-xs font-medium text-subtle">
            <span aria-hidden="true" className="line-draw h-px flex-1 origin-right bg-line" />
            <span className="flex items-center gap-2 whitespace-nowrap">
              <OrbitMark />
              {strings.endOfList}
            </span>
            <span aria-hidden="true" className="line-draw h-px flex-1 origin-left bg-line" />
          </p>
        )}
      </div>
    </ListAnalyticsContext.Provider>
  );
}

function OrbitMark() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4" fill="none">
      <circle cx="10" cy="10" r="3.5" fill="currentColor" />
      <ellipse
        cx="10"
        cy="10"
        rx="8"
        ry="3"
        transform="rotate(-24 10 10)"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeOpacity="0.7"
      />
    </svg>
  );
}
